import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { UtilityService } from '../../services/utility.service';
import { FormlyErrorComponent } from '../formly-error.component';
import { UserStore } from 'app/core/services/user.store';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { finalize, switchMap, tap } from 'rxjs';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { PrivateDocumentService } from '../../services/private-document.service';
import { LinkButtonComponent } from '../link-button.component';
import { UploadPanelComponent } from '../upload/upload-panel.component';
import { UploadedFileRowComponent } from '../upload/uploaded-file-row.component';
import { buildFileMeta } from '../upload/upload-meta.util';
import { fileTypeIcon } from '../upload/file-type-icon.util';
import { normalizeAcceptedTypes, resolveMaxFileSizeMb, validateUploadFiles } from '../upload/upload-validation.util';
import { commitUserValue } from './user-edit.util';

interface DocumentEntry extends Entry {
  entry: {
    archived: boolean;
    user?: any;
    company?: any;
    location?: any;
    moduleId?: string;
    entryId?: string;
    share?: string;
    name: string;
    description?: string;
    'end-date'?: string;
    'document-type'?: any;
    file?: {
      documentName?: string;
      documentId?: string;
      blobUrl?: string;
      blobReference?: string;
      createdDateTime?: Date;
      /** Bytes, written by this field — see createDocumentEntry() for why. */
      size?: number;
    };
  };
}

interface UploadState {
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
}

interface DocumentMetadata {
  documentName?: string;
  documentId?: string;
  blobUrl?: string;
  blobReference?: string;
  createdDateTime?: string;
}

@Component({
  selector: 'formly-field-upload-document',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatCheckboxModule,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    UploadPanelComponent,
    UploadedFileRowComponent,
    LinkButtonComponent,
  ],
  template: `
    <div class="flex flex-col mb-4" [formlyDataCy]="field">
      <!--
        The Figma "File upload · Type=Button" panel owns the label, the hint, the upload button and
        the error message; the uploaded-file card is projected into it. The dropzone variant is not
        used here — see upload-panel.component.ts for why the two are separate components.
      -->
      <app-upload-panel
        [label]="props.label"
        [required]="!!props.required"
        [hint]="panelHint()"
        [accept]="acceptAttribute()"
        [buttonLabel]="hasDocument() ? 'Vervangen' : 'Bestand uploaden'"
        [disabled]="isDisabled()"
        [error]="uploadState().error ?? ''"
        (filesSelected)="onFileSelected($event)">
        @if (uploadState().isUploading) {
          <!--
            The same card, filled with what we already know while the server works: the file the
            user just picked and its size. This branch comes first so a replacement shows the NEW
            file rather than the one it is about to push aside, and so no action button is live
            mid-flight. Figma's State=Uploading hides its progress bar too.
          -->
          <app-uploaded-file-row [fileName]="selectedFileName()" [meta]="pendingMeta()" state="uploading" />
        } @else if (document(); as doc) {
          <app-uploaded-file-row
            [fileName]="doc.entry?.name || getDocumentMetadata(doc)?.documentName || ''"
            [meta]="documentMeta()"
            state="uploaded"
            [thumbnailUrl]="getDocumentImageUrl(doc)"
            [previewable]="true"
            [previewIcon]="isImageDocument(doc) ? 'heroicons_outline:eye' : 'heroicons_outline:arrow-down-tray'"
            [previewLabel]="isImageDocument(doc) ? 'Voorbeeld bekijken' : 'Document downloaden'"
            [removable]="!isDisabled()"
            [removeLabel]="'Document verwijderen'"
            (preview)="openDocument(doc)"
            (remove)="deleteDocumentConformation(doc)">
            @if (descriptionForCard(doc); as description) {
              <!-- Description captured at upload time — distinct from props.description below,
                   which reflects the current field config, not what this specific file was tagged
                   with. Styled to match the row's own meta line. -->
              <div class="mt-2 text-[12px] leading-[1.4] text-accent">{{ description }}</div>
            }
            @if (isDocumentPublic(doc)) {
              <div class="mt-2 flex flex-wrap items-center gap-3.5">
                <button dd-link-button type="button" icon="feather:clipboard" (click)="copyLink(doc)">
                  Link kopiëren
                </button>
              </div>
            }
          </app-uploaded-file-row>
        }
      </app-upload-panel>

      <!-- Privacy toggle -->
      @if (!isDisabled() && !hasDocument() && showPrivateOptions()) {
        <div class="mt-2 mb-4" [hidden]="uploadState().isUploading">
          <mat-checkbox [checked]="isPublic()" (change)="togglePrivate($event.checked)">
            Publiek document
          </mat-checkbox>
        </div>
      }

      @if (props.description && !uploadState().isUploading) {
        <div class="mt-2 text-sm text-accent">
          {{ props.description }}
        </div>
      }

      @if (field.formControl.errors) {
        <app-formly-error [field]="field"></app-formly-error>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldUploadDocumentComponent extends FieldType implements OnInit {
  private modulesStore = inject(ModulesStore);
  private entriesService = inject(EntriesService);
  private utilityService = inject(UtilityService);
  private userStore = inject(UserStore);
  private fuseConfirmationService = inject(FuseConfirmationService);
  private privateDocument = inject(PrivateDocumentService);

  // State management with signals
  uploadState = signal<UploadState>({
    isUploading: false,
    uploadProgress: 0,
    error: null,
  });

  private documentSignal = signal<DocumentEntry | null>(null);
  selectedFileName = signal<string>('');

  /** Seeded from the picked file; the entry is created with it straight away. */
  documentName = '';

  // Image type checking
  private readonly imageExtensions = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']);
  private readonly imageMimeTypes = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/svg+xml',
  ]);

  // Computed properties
  showPrivateOptions = computed(() => {
    return this.field.props.uploadType === 'public-and-private';
  });

  document = computed(() => this.documentSignal());
  isPublic = signal(false);
  hasDocument = computed(() => this.document() !== null);

  // Form state ophalen uit options
  private entity = computed(() => this.options?.formState?.entity);
  private entry = computed(() => this.options?.formState?.entry);
  private isNew = computed(() => this.options?.formState?.isNew);

  // Document module ID
  private documentModuleId = computed(() => {
    return this.modulesStore.moduleCodeToId('documents');
  });

  ngOnInit() {
    this.initializeUploadType();
    this.initializeDocuments();
  }

  private initializeUploadType(): void {
    if (this.field.props.uploadType === 'public') {
      this.isPublic.set(true);
    } else if (this.field.props.uploadType === 'private') {
      this.isPublic.set(false);
    }
  }

  private initializeDocuments(): void {
    const currentValue = this.field.formControl.value;
    if (currentValue && typeof currentValue === 'object') {
      this.documentSignal.set(currentValue);
      this.#adoptPrivacyOf(currentValue);
    }
  }

  /**
   * Take over the privacy of the document that is already attached.
   *
   * The checkbox only renders while the field is empty, so once a document hangs there the user
   * has no way to state a preference — and the panel does offer a replace button. Without this the
   * default (private) would win and replacing a public document would silently make it private,
   * breaking any link that was shared from it.
   *
   * Only when the field leaves the choice open: with uploadType 'public' or 'private' the field
   * config is absolute and an older document must not override it.
   */
  #adoptPrivacyOf(document: DocumentEntry | null): void {
    if (!this.showPrivateOptions()) return;

    const share = document?.entry?.share;
    if (share) {
      this.isPublic.set(share === 'public');
    }
  }

  /** `accept` attribute for the hidden input, from the configured accepted types. */
  protected readonly acceptAttribute = computed(() => normalizeAcceptedTypes(this.props?.['acceptedTypes']).join(','));

  /**
   * The line under the label: accepted types and the ceiling, as Figma shows it ("png, jpeg or
   * tiff • max 10 gb"). Always this — the configured instruction text is a separate thing and
   * renders below the panel, so folding it in here would print it twice (#20817 asks for both).
   */
  protected readonly panelHint = computed(() => {
    // Name the ceiling here — it is enforced in #validateFiles, and without it the user only
    // discovers the limit by having an upload rejected.
    const types = normalizeAcceptedTypes(this.props?.['acceptedTypes']);
    const kinds = types.length ? types.join(', ') : 'Alle bestandstypen';
    return `${kinds} • tot ${this.#maxFileSizeMb()} MB`;
  });

  /** Fallback for entries written before the size was persisted; see createDocumentEntry(). */
  readonly #uploadedSizeBytes = signal<number | null>(null);

  /**
   * Ticks so the relative time in the meta line keeps up: `buildFileMeta` is pure, so without this
   * "zojuist" would still be on screen an hour after the upload. Half a minute catches the first
   * transition ("zojuist" → "1 minuut geleden") without polling hard. Kept local because this is
   * the only surface that shows a live age; lift it to a shared ticker if a second one appears.
   */
  readonly #tick = signal(0);

  constructor() {
    super();
    const ticker = setInterval(() => {
      // Nothing on screen to age, nothing to recompute.
      if (this.document()) this.#tick.update(value => value + 1);
    }, 30_000);
    inject(DestroyRef).onDestroy(() => clearInterval(ticker));
  }

  /**
   * Meta line while the upload is in flight: only the size is known, and an age would be a lie
   * because nothing has been stored yet.
   */
  protected readonly pendingMeta = computed(() => buildFileMeta({ sizeBytes: this.#uploadedSizeBytes() }));

  protected readonly documentMeta = computed(() => {
    this.#tick();
    return buildFileMeta({
      // Prefer the persisted size: the session value only lives in the instance that performed the
      // upload, and this field is rebuilt as soon as the form control changes — which is exactly
      // why the meta line used to collapse to just the age (#20911).
      sizeBytes: this.document()?.entry?.file?.size ?? this.#uploadedSizeBytes(),
      createdAt: this.getDocumentMetadata(this.document())?.createdDateTime ?? null,
    });
  });

  /**
   * Reject a selection before anything is uploaded, or null when it may proceed. Delegates to the
   * shared `validateUploadFiles` (upload-validation.util.ts) so this field and the
   * required-documents checklist enforce identical rules — see that function's doc comment for
   * why this is a UX guard, not a security boundary.
   */
  #validateFiles(files: FileList): string | null {
    const result = validateUploadFiles(
      files,
      this.props?.['acceptedTypes'] as string[] | string | undefined,
      this.props?.['maxFileSizeMb'] as number | undefined
    );
    return result.error ?? null;
  }

  /** Configured size limit in MB; falls back to the default rather than leaving uploads unbounded. */
  #maxFileSizeMb(): number {
    return resolveMaxFileSizeMb(this.props?.['maxFileSizeMb'] as number | undefined);
  }

  onFileSelected(files: FileList) {
    if (!files || files.length === 0) return;

    this.field.formControl.markAsTouched();

    // Validate here rather than in each caller: the picker and the dropzone both land here, and
    // the input's `accept` attribute only filters the picker dialog — it enforces nothing.
    const error = this.#validateFiles(files);
    if (error) {
      this.uploadState.update(state => ({ ...state, error }));
      this.selectedFileName.set('');
      // Also clear the name: it is seeded from the PREVIOUS pick, and a rejected second file must
      // not leave the first file's name behind for the next upload to reuse.
      this.documentName = '';
      return;
    }

    const file = files[0];
    this.selectedFileName.set(file.name);
    // Only now, past validation: a rejected pick must not rewrite the size shown for the file that
    // is still attached.
    this.#uploadedSizeBytes.set(file.size ?? null);
    this.documentName = this.extractFileNameWithoutExtension(file.name);

    this.startUpload(files);
  }

  private extractFileNameWithoutExtension(fileName: string): string {
    return fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
  }

  private startUpload(file: FileList): void {
    // The panel keeps its button once a file is attached, so this can be a replacement. Remember
    // what hung here before: the new upload creates its own document entry, and without this the
    // previous one would stay behind in the documents module with its blob, referenced by nothing.
    const replaced = this.document();

    this.uploadState.set({
      isUploading: true,
      uploadProgress: 0,
      error: null,
    });

    this.createDocumentEntry(file)
      .pipe(
        tap((documentEntry: DocumentEntry) => this.updateFormControl(documentEntry)),
        finalize(() => {
          this.uploadState.update(state => ({
            ...state,
            isUploading: false,
          }));
        })
      )
      .subscribe({
        next: documentEntry => {
          this.utilityService.notify('Document succesvol geüpload');
          this.#archiveReplaced(replaced);
          this.uploadState.update(state => ({
            ...state,
            uploadProgress: 100,
          }));
        },
        error: error => {
          console.error('Upload error:', error);
          this.uploadState.update(state => ({
            ...state,
            error: 'Er is een fout opgetreden bij het uploaden van het document.',
          }));
        },
      });
  }

  /**
   * Archive the document a replacement pushed aside, rather than deleting it: the file was really
   * uploaded once and archiving leaves that trail, which a hard delete would not. Silent on
   * purpose — the user asked to replace a file, not to manage the old one — but a failure is
   * surfaced, because a stuck entry is worth knowing about.
   */
  #archiveReplaced(replaced: DocumentEntry | null): void {
    if (!replaced?.id) return;

    this.modulesStore.archiveEntry({
      archive: true,
      data: replaced,
      moduleId: this.documentModuleId(),
      showSnackBarMessage: false,
      onError: () => this.utilityService.notify('Het vervangen document kon niet worden gearchiveerd.'),
    });
  }

  private createDocumentEntry(file: FileList) {
    const documentData: DocumentEntry['entry'] = {
      archived: false,
      name: this.documentName || file[0].name,
      description: this.field.props.description || '',
      user: this.userStore.currentUserEntry(),
      company: this.userStore.currentCompanyEntry(),
      location: this.userStore.currentLocationEntry(),
      'document-type': this.field.props.documentType || null,
      moduleId: this.entity()?.id,
      entryId: this.entry()?.id,
      share: this.isPublic() ? 'public' : 'private',
      file: {
        documentName: this.documentName || file[0].name,
        // The API model has no size field (#20817), so persist it in the entry bag ourselves.
        // Without it the meta line can only show the age once the field is rebuilt or reloaded,
        // while the design calls for "3 MB • 1 minuut geleden" at all times.
        size: file[0].size,
      },
    };

    if (this.field.props.documentType) {
      documentData['document-type'] = this.field.props.documentType;
    }

    if (this.field.props.endDate) {
      documentData['end-date'] = this.field.props.endDate;
    }

    const files = {
      fieldKey: this.field.props.keyPath,
      files: file,
      isPublic: this.isPublic(),
    };

    return this.entriesService.addEntry(this.documentModuleId(), documentData, files).pipe(
      tap(() => {
        this.uploadState.update(state => ({
          ...state,
          uploadProgress: 30,
        }));
      })
    );
  }

  private updateFormControl(documentEntry: DocumentEntry): void {
    this.documentSignal.set(documentEntry);
    // Every caller is a user action (upload finished, delete confirmed), so the entry control has
    // to be marked edited too — otherwise the save button stays disabled (#21026).
    commitUserValue(this.field.formControl, documentEntry);
    // Keep the privacy state pointing at what is attached now, so a second replacement inherits
    // from the document it actually replaces rather than from the one before it.
    this.#adoptPrivacyOf(documentEntry);
  }

  // Image-related methods
  isImageDocument(document: DocumentEntry): boolean {
    if (!document) return false;

    const metadata = this.getDocumentMetadata(document);
    if (!metadata?.blobReference) return false;

    const extension = this.getFileExtension(metadata.blobReference);
    return this.imageExtensions.has(extension.toLowerCase());
  }

  getDocumentImageUrl(document: DocumentEntry): string | null {
    if (!this.isImageDocument(document)) return null;

    const metadata = this.getDocumentMetadata(document);
    return metadata?.blobUrl || null;
  }

  openImagePreview(document: DocumentEntry): void {
    if (!this.isImageDocument(document)) return;

    const metadata = this.getDocumentMetadata(document);
    if (!metadata) return;

    // Public: directly open the blob URL in a new tab (browser sends cookies).
    if (metadata.blobUrl) {
      window.open(metadata.blobUrl, '_blank', 'noopener');
      return;
    }

    // Private: the backend returns the file URL as JSON; PrivateDocumentService
    // fetches the image itself with a correct Origin and opens it inline.
    if (!metadata.documentId) return;

    this.privateDocument.openImagePreview(this.documentModuleId(), document.id, metadata.documentId).subscribe({
      error: error => {
        console.error('Failed to open image preview:', error);
        this.utilityService.notify('Kon voorbeeld niet openen');
      },
    });
  }

  onImageLoadError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';
  }

  getDocumentIcon(document: DocumentEntry): string {
    if (this.isImageDocument(document)) {
      return 'image';
    }

    const metadata = this.getDocumentMetadata(document);
    if (!metadata?.blobReference) return 'description';

    // One canonical extension->icon mapping, shared with the read-view Upload card (#21243).
    return fileTypeIcon(metadata.blobReference);
  }

  getFileTypeLabel(fileName: string | null | undefined): string {
    if (!fileName) return 'File';

    const extension = this.getFileExtension(fileName);

    switch (extension.toLowerCase()) {
      case '.pdf':
        return 'PDF Document';
      case '.doc':
      case '.docx':
        return 'Word Document';
      case '.xls':
      case '.xlsx':
        return 'Excel Spreadsheet';
      case '.ppt':
      case '.pptx':
        return 'PowerPoint Presentation';
      case '.jpg':
      case '.jpeg':
        return 'JPEG Image';
      case '.png':
        return 'PNG Image';
      case '.gif':
        return 'GIF Image';
      case '.webp':
        return 'WebP Image';
      case '.mp4':
        return 'MP4 Video';
      case '.mp3':
        return 'MP3 Audio';
      case '.zip':
        return 'ZIP Archive';
      default:
        return `${extension.toUpperCase().substring(1)} File`;
    }
  }

  private getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
  }

  togglePrivate(checked: boolean): void {
    this.isPublic.set(checked);
  }

  clearError(): void {
    this.uploadState.update(state => ({
      ...state,
      error: null,
    }));
  }

  isDisabled(): boolean {
    return this.field.props.disabled || this.form.disabled;
  }

  deleteDocumentConformation(document: DocumentEntry): void {
    if (document.entry == undefined) {
      console.warn('Document entry is undefined, cannot delete.');
      this.updateFormControl(null);
      return;
    }

    const confirmation = this.fuseConfirmationService.open({
      title: 'Document verwijderen',
      message: `Weet je zeker dat je '${document.entry.name}' wil verwijderen?`,
      actions: {
        cancel: { label: 'Annuleren' },
        confirm: { label: 'Verwijderen' },
      },
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.deleteDocument(document);
      }
    });
  }

  private deleteDocument(document: DocumentEntry): void {
    this.entriesService.delete(this.documentModuleId(), document.id).subscribe({
      next: () => {
        this.documentSignal.set(null);
        commitUserValue(this.field.formControl, null);
        this.utilityService.notify('Document verwijderd');
      },
      error: error => {
        console.error('Delete error:', error);
        if (error.status === 400 || error.status === 404) {
          this.documentSignal.set(null);
          commitUserValue(this.field.formControl, null);
          this.utilityService.notify('Document verwijderd');
          return;
        }
        this.utilityService.notify('Er is een fout opgetreden bij het verwijderen van het document.');
      },
    });
  }

  downloadDocument(document: DocumentEntry): void {
    const documentMetadata = this.getDocumentMetadata(document);
    if (documentMetadata) {
      this.modulesStore.downloadDocument({
        moduleId: this.documentModuleId(),
        entryId: document.id,
        documentId: documentMetadata.documentId,
        // Prefer the entry's display name (the title shown in the UI); the
        // metadata documentName is often empty, which falls back to the raw
        // blob file name (a GUID). The service appends the real extension.
        documentName: document.entry?.name || documentMetadata.documentName,
        blobUrl: documentMetadata.blobUrl,
      });
    }
  }

  copyLink(document: DocumentEntry): void {
    const documentMetadata = this.getDocumentMetadata(document);
    if (documentMetadata?.blobUrl) {
      navigator.clipboard.writeText(documentMetadata.blobUrl);
      this.utilityService.notify('Link gekopieerd naar klembord');
    }
  }

  /**
   * The description captured on the document itself, but only when it says something the field
   * config does not already say below the panel. `createDocumentEntry` seeds it from
   * `props.description`, so for anything uploaded under the current config the two are identical
   * and rendering both would print the same sentence twice.
   */
  descriptionForCard(document: DocumentEntry): string {
    const captured = document.entry?.description ?? '';
    return captured && captured !== (this.props?.description ?? '') ? captured : '';
  }

  /**
   * The card's single action button next to remove. Figma shows exactly two icon buttons, so the
   * separate download link is gone; an image opens its preview and anything else downloads, which
   * keeps a PDF reachable from the form.
   */
  openDocument(document: DocumentEntry): void {
    if (this.isImageDocument(document)) {
      this.openImagePreview(document);
      return;
    }
    this.downloadDocument(document);
  }

  isDocumentPublic(document: DocumentEntry): boolean {
    return document?.entry?.share === 'public' || (document?.publicDocuments && document.publicDocuments.length > 0);
  }

  getDocumentMetadata(document: DocumentEntry): DocumentMetadata | null {
    // Prefer the candidate that carries a documentId (the private/public arrays can
    // be present but empty/partial; the resolved id lives in entry.file). Falling
    // back to a candidate without documentId previously produced
    // `.../documents/undefined/private` 404s.
    const candidates = [
      document.publicDocuments?.[0],
      document.privateDocuments?.[0],
      document.entry?.file as unknown as DocumentMetadata | undefined,
    ];
    return candidates.find(meta => meta?.documentId) ?? candidates.find(Boolean) ?? null;
  }
}
